import type { Account } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { createCloudProviderCondition } from '../cloudProviders/createCloudProviderCondition';
import { Loader } from '../layout/loading';
import { CopyableCode } from '../shared/copyableCode';

export const VaultOwnBucketCopyableAllAccountsPolicy = ({
  bucketName,
}: {
  bucketName: string;
}) => {
  const dal = useDAL();
  const { data: restoreAccountsBody, isLoading: restoreAccountsLoading } =
    dal.cloudAccounts.restore.useList(
      undefined,
      undefined,
      undefined,
      createCloudProviderCondition([Provider.Aws])
    );

  if (restoreAccountsLoading) {
    return <Loader />;
  }

  return (
    <VaultOwnBucketCopyablePolicy
      bucketName={bucketName}
      restoreAccounts={restoreAccountsBody?.accounts ?? []}
    />
  );
};

export const VaultOwnBucketCopyablePolicy = ({
  bucketName,
  restoreAccounts,
}: {
  bucketName: string;
  restoreAccounts: Account[];
}) => {
  const dal = useDAL();
  const { dlsgFrontend } = useFeatureFlags();
  const { data: scanningAccounts, isLoading: isLoadingScanningAccounts } =
    dal.cloudAccounts.scanning.useList();

  if (isLoadingScanningAccounts) {
    return <Loader />;
  }

  const scanningAccountId =
    scanningAccounts?.accounts.find(
      (account) => account.cloudProvider === Provider.Aws
    )?.providerAccountId || '';

  return (
    <CopyableCode
      title='policy.json'
      code={getCode(
        bucketName,
        scanningAccountId,
        dlsgFrontend,
        restoreAccounts
      )}
      maxHeight='320px'
      blur={!bucketName}
    />
  );
};

const getCode = (
  bucketName: string,
  scanningAccountId: string,
  dlsgFrontend: boolean,
  restoreAccounts?: Account[]
) => {
  const scanningAccountRoles = dlsgFrontend
    ? `[
    "arn:aws:iam::${scanningAccountId}:role/EonScanningNodeRole",
    "arn:aws:iam::${scanningAccountId}:role/S3ProxyServiceRole",
    "arn:aws:iam::${scanningAccountId}:role/eon-gw-data-access-role"
  ]`
    : `"arn:aws:iam::${scanningAccountId}:role/EonScanningNodeRole"`;
  return `
  {
    "Version": "2012-10-17",
    "Statement": [
      {
          "Effect": "Allow",
          "Principal": {
              "AWS": ${scanningAccountRoles}
          },
          "Action": [
              "s3:GetObject",
              "s3:PutObject",
              "s3:DeleteObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ]
      }${restoreAccounts?.length ? getRestoreAccountsCode(restoreAccounts, bucketName) : ''}
    ]
}`;
};

const getRestoreAccountsCode = (
  restoreAccounts: Account[],
  bucketName: string
) => {
  return `,
      {
          "Sid": "AllowRestoreAccess",
          "Effect": "Allow",
          "Principal": {
                  "AWS": [${restoreAccounts.map((account) => `"arn:aws:iam::${account.providerAccountId}:role/EonRestoreNodeRole"`).join(',')}]
          },
          "Action": [
              "s3:GetObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ]
      },
      {
          "Sid": "AthenaGetSchemaJson",
          "Effect": "Allow",
          "Principal": {
              "AWS": [${restoreAccounts.map((account) => `"${account.role}"`).join(',')}]
          },
          "Action": "s3:GetObject",
          "Resource": [
              "arn:aws:s3:::${bucketName}/dynamodb/*/eon-*/*/*/_schema.json",
              "arn:aws:s3:::${bucketName}/db-export/*/eon-*/*/*/_schema.json",
              "arn:aws:s3:::${bucketName}/*/db-export/*/eon-*/*/*/_schema.json"
          ]
      },
      {
          "Sid": "AthenaQueryPolicy",
          "Effect": "Allow",
          "Principal": {
              "AWS": [${restoreAccounts.map((account) => `"${account.role}"`).join(',')}]
          },
          "Action": [
              "s3:GetObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ],
          "Condition": {
              "ForAllValues:StringEquals": {
                  "aws:CalledVia": "athena.amazonaws.com"
              }
          }
      }`;
};
